import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

type AgencyMetrics = {
  jobsManaged: number;
  kolProfiles: number;
  invitationsTotal: number;
  totalBudgetManagedRM: number;
  kolPaymentsTotalRM: number;
  kolPaymentsCount: number;
  managedJobsByStatus: Record<string, number>;
  invitationByStatus: Record<string, number>;
};

type OrgDetailResponse = {
  organization: {
    id: string;
    name: string;
    type: string;
    createdAt: string;
    updatedAt: string;
  };
  counts: {
    users: number;
    jobs: number;
    jobsManaged: number;
    kolProfiles: number;
    invitations: number;
    jobTemplates: number;
    contractTemplates: number;
    savedSearches: number;
  };
  metrics: AgencyMetrics | Record<string, unknown>;
  members: Array<{
    membershipId: string;
    roleInOrg: string;
    createdAt: string;
    user: {
      id: string;
      email: string;
      fullName: string | null;
      role: string;
      phone: string | null;
      createdAt: string;
    };
  }>;
  kolProfiles: Array<{
    id: string;
    name: string;
    email: string | null;
    userId: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  recentJobsManaged: Array<{
    id: string;
    title: string;
    status: string;
    createdAt: string;
    totalBudgetRM: number | null;
    brandOrg: { id: string; name: string };
  }>;
};

export default function AdminAgencyDetail() {
  const { agencyId } = useParams<{ agencyId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<OrgDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    if (!agencyId) return;
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data: res } = await api.get<OrgDetailResponse>(`/admin/organizations/${agencyId}`);
        if (!active) return;
        if (res.organization.type !== 'AGENCY') {
          setError('This organization is not an agency.');
          setData(null);
          return;
        }
        setData(res);
        setEditName(res.organization.name);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        if (!active) return;
        setError(e.response?.data?.error || e.message || 'Failed to load agency');
        setData(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [agencyId]);

  const metrics = data?.metrics as AgencyMetrics | undefined;

  const saveName = async () => {
    if (!agencyId || !editName.trim()) return;
    setSavingName(true);
    try {
      await api.patch(`/admin/organizations/${agencyId}`, { name: editName.trim() });
      setData((prev) =>
        prev
          ? {
              ...prev,
              organization: { ...prev.organization, name: editName.trim() },
            }
          : prev
      );
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      alert(e.response?.data?.error || 'Could not update name');
    } finally {
      setSavingName(false);
    }
  };

  if (!agencyId) {
    return (
      <div className="p-6 text-red-600 dark:text-red-400">Missing agency id.</div>
    );
  }

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <button
          type="button"
          onClick={() => navigate('/admin/agencies')}
          className="text-sm text-accent2 hover:underline mb-2"
        >
          ← All agencies
        </button>
        {loading ? (
          <h1 className="page-title">Loading…</h1>
        ) : data ? (
          <>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h1 className="page-title">{data.organization.name}</h1>
                <p className="text-glass-text-3 mt-1 font-mono text-sm">{data.organization.id}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={`/admin/campaigns?agencyOrgId=${agencyId}`}
                  className="btn-glass inline-flex items-center px-4 py-2 text-sm font-medium"
                >
                  Filter campaigns
                </Link>
                <Link
                  to={`/admin/users?organizationId=${agencyId}`}
                  className="btn-glass inline-flex items-center px-4 py-2 text-sm font-medium"
                >
                  Filter users
                </Link>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 items-start sm:items-end">
              <div className="flex-1 max-w-md">
                <label className="block text-xs font-medium text-glass-text-3 mb-1">
                  Agency name (platform admin)
                </label>
                <div className="flex gap-2">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1" />
                  <Button type="button" disabled={savingName || editName.trim() === data.organization.name} onClick={() => void saveName()}>
                    {savingName ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <h1 className="page-title text-red-500">Agency not found</h1>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>
      )}

      {data && metrics && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card p-5">
              <div className="text-sm text-glass-text-2">Managed campaigns</div>
              <div className="stat-display text-2xl mt-1">{metrics.jobsManaged}</div>
            </div>
            <div className="glass-card p-5">
              <div className="text-sm text-glass-text-2">KOL profiles</div>
              <div className="stat-display text-2xl mt-1 text-[rgb(var(--pos))]">{metrics.kolProfiles}</div>
            </div>
            <div className="glass-card p-5">
              <div className="text-sm text-glass-text-2">Campaign budget (sum RM)</div>
              <div className="stat-display text-2xl mt-1 text-accent2">
                {metrics.totalBudgetManagedRM.toLocaleString()}
              </div>
            </div>
            <div className="glass-card p-5">
              <div className="text-sm text-glass-text-2">KOL payments (sum RM)</div>
              <div className="stat-display text-2xl mt-1">
                {metrics.kolPaymentsTotalRM.toLocaleString()}{' '}
                <span className="text-sm font-normal text-glass-text-3">({metrics.kolPaymentsCount} records)</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-card overflow-hidden">
              <div className="px-5 py-4 border-b border-glass font-semibold text-glass-text">Campaigns by status</div>
              <ul className="divide-y divide-glass">
                {Object.entries(metrics.managedJobsByStatus || {}).map(([k, v]) => (
                  <li key={k} className="px-5 py-3 flex justify-between text-sm">
                    <span className="text-glass-text-2">{k}</span>
                    <span className="font-medium text-glass-text">{v}</span>
                  </li>
                ))}
                {Object.keys(metrics.managedJobsByStatus || {}).length === 0 && (
                  <li className="px-5 py-6 text-sm text-glass-text-2 text-center">No managed campaigns yet.</li>
                )}
              </ul>
            </div>
            <div className="glass-card overflow-hidden">
              <div className="px-5 py-4 border-b border-glass font-semibold text-glass-text">Invitations by status</div>
              <ul className="divide-y divide-glass">
                {Object.entries(metrics.invitationByStatus || {}).map(([k, v]) => (
                  <li key={k} className="px-5 py-3 flex justify-between text-sm">
                    <span className="text-glass-text-2">{k}</span>
                    <span className="font-medium text-glass-text">{v}</span>
                  </li>
                ))}
                {Object.keys(metrics.invitationByStatus || {}).length === 0 && (
                  <li className="px-5 py-6 text-sm text-glass-text-2 text-center">No invitations for this agency.</li>
                )}
              </ul>
            </div>
          </div>

          <div className="glass-card overflow-hidden">
            <div className="px-5 py-4 border-b border-glass font-semibold text-glass-text">
              Team members ({data.members.length})
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-glass bg-glass-2/30">
                  <tr>
                    <th className="text-left px-4 py-2 text-glass-text-3">User</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Global role</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Org role</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-glass">
                  {data.members.map((m) => (
                    <tr key={m.membershipId} className="hover:bg-glass-3/30">
                      <td className="px-4 py-3">
                        <div className="font-medium text-glass-text">{m.user.fullName || m.user.email}</div>
                        <div className="text-xs text-glass-text-3 font-mono">{m.user.id}</div>
                      </td>
                      <td className="px-4 py-3 text-glass-text-2">{m.user.role}</td>
                      <td className="px-4 py-3 text-glass-text-2">{m.roleInOrg}</td>
                      <td className="px-4 py-3 text-glass-text-2">{new Date(m.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="glass-card overflow-hidden">
            <div className="px-5 py-4 border-b border-glass font-semibold text-glass-text">
              KOL roster (latest {data.kolProfiles.length})
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-glass bg-glass-2/30">
                  <tr>
                    <th className="text-left px-4 py-2 text-glass-text-3">Name</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Email</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Linked user</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-glass">
                  {data.kolProfiles.map((k) => (
                    <tr key={k.id} className="hover:bg-glass-3/30">
                      <td className="px-4 py-3 font-medium text-glass-text">{k.name}</td>
                      <td className="px-4 py-3 text-glass-text-2">{k.email || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-glass-text-3">{k.userId || '—'}</td>
                      <td className="px-4 py-3 text-glass-text-2">{new Date(k.updatedAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {data.kolProfiles.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-glass-text-2">
                        No KOL profiles.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="glass-card overflow-hidden">
            <div className="px-5 py-4 border-b border-glass font-semibold text-glass-text">Recent managed campaigns</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-glass bg-glass-2/30">
                  <tr>
                    <th className="text-left px-4 py-2 text-glass-text-3">Title</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Brand</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Budget RM</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Status</th>
                    <th className="text-left px-4 py-2 text-glass-text-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-glass">
                  {data.recentJobsManaged.map((j) => (
                    <tr key={j.id} className="hover:bg-glass-3/30">
                      <td className="px-4 py-3 font-medium text-glass-text">{j.title}</td>
                      <td className="px-4 py-3 text-glass-text-2">{j.brandOrg.name}</td>
                      <td className="px-4 py-3 text-glass-text">{(j.totalBudgetRM ?? 0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className="status-pill status-pill-info">{j.status}</span>
                      </td>
                      <td className="px-4 py-3 text-glass-text-2">{new Date(j.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {data.recentJobsManaged.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-glass-text-2">
                        No campaigns managed by this agency yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-xs text-glass-text-3 space-y-1">
            <p>
              Templates: {data.counts.jobTemplates} job · {data.counts.contractTemplates} contract ·{' '}
              {data.counts.savedSearches} saved searches
            </p>
            <p>
              Org created {new Date(data.organization.createdAt).toLocaleString()} · updated{' '}
              {new Date(data.organization.updatedAt).toLocaleString()}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
